import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAssignedCoordinatorReservations, useBusinesses } from '@/hooks/api-hooks';
import { formatDayLong, getUrlParam, setWebTitle, todayStart } from '@/lib/format-utils';
import SearchInput from '@/components/search-input';
import { useSearchParams } from 'react-router-dom';
import { Loader, Button } from '@mantine/core';
import ReservationsList from '@/components/reservations-list';
import BusinessSelector from '@/components/business-selector';
import { Business } from '@turizi/shared';
import { IconBuildingStore, IconMailForward } from '@tabler/icons-react';
import { multiWordSearch } from '@/utils/search-utils';
import DatePickerCustom from '@/components/custom-date-picker';
import SendAssignmentMailModal from '@/components/send-assignment-mail-modal';


export default function AssignmentsPage(): JSX.Element {
    const { t } = useTranslation();

    setWebTitle(t('assignments'));

    const [searchText, setSearchText] = useState('');
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedBusinessId = searchParams.get('business_id') || '';
    const isBusinessSelected = Boolean(selectedBusinessId);

    const [start, setStart] = useState<string | null>(getUrlParam('start', todayStart()));

    // Email Modal state
    const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);

    const { data: businessesRes } = useBusinesses();
    const businesses = businessesRes?.data || [];

    const selectedBusiness = useMemo(() => {
        if (!selectedBusinessId) return null;
        return businesses.find((b) => String(b.id) === selectedBusinessId) || null;
    }, [businesses, selectedBusinessId]);

    const handleSelectBusiness = (b: Business) => {
        const newParams = new URLSearchParams(searchParams);
        if (b && b.id) {
            newParams.set('business_id', String(b.id));
        } else {
            newParams.delete('business_id');
        }
        setSearchParams(newParams);
    };

    const params = useMemo(() => {
        return {
            start,
            business_id: isBusinessSelected ? selectedBusinessId : undefined
        };
    }, [start, selectedBusinessId, isBusinessSelected]);

    const { data: res, isLoading } = useAssignedCoordinatorReservations(params, { enabled: isBusinessSelected });

    const rawReservations = res?.data || [];

    // Filter by text search if entered
    const filteredReservations = useMemo(() => {
        if (!searchText.trim()) return rawReservations;
        return rawReservations.filter((reservation) => {
            const clientName = reservation.client_business?.name || '';
            const resId = String(reservation.id);
            const resGroupsText = (reservation.pax_groups || [])
                .flatMap((g) => [g.name || '', ...(g.persons || []).map((p) => p.name || '')])
                .join(' ');
            const bookingsSearchText = (reservation.service_bookings || [])
                .map((b) => {
                    const groupNames = (b.pax_groups || []).flatMap((pg) => [
                        pg.pax_group?.name || '',
                        ...(pg.pax_group?.persons || []).map((p) => p.name || '')
                    ]);
                    return `${b.service_data?.name || ''} ${groupNames.join(' ')}`;
                })
                .join(' ');
            const fullText = `${resId} ${clientName} ${resGroupsText} ${bookingsSearchText}`;
            return multiWordSearch(fullText, searchText);
        });
    }, [rawReservations, searchText]);

    return (
        <div className="panel-content space-y-6">
            {/* Top Toolbar / Filters */}
            <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-4">

                    <div className="flex items-center gap-4 flex-wrap">
                        {/* Business Selector */}
                        <div className="flex items-center gap-2">
                            <div className="w-64">
                                <BusinessSelector
                                    selected={selectedBusiness}
                                    onSelect={handleSelectBusiness}
                                    className='font-bold'
                                />
                            </div>
                        </div>

                        {/* Single Date Selector */}
                        <div className="flex items-center gap-2">
                            <DatePickerCustom
                                value={start}
                                onChange={(val) => setStart(val[0])}
                                clearable={false}
                                hideLabel={true}
                                valueFormat='DD MMM YYYY'
                                className="font-bold"
                            />
                        </div>
                    </div>
                </div>
                <SearchInput
                    onSearch={setSearchText}
                    placeholder={t('search_placeholder')}
                />
            </div>

            {/* Unselected state */}
            {!isBusinessSelected && (
                <div className="text-center space-y-4">
                    <div className="w-12 h-12 bg-slate-50 text-slate-400 rounded-full flex items-center justify-center mx-auto">
                        <IconBuildingStore size={24} />
                    </div>
                    <h3 className="text-sm font-bold text-gray-800">{t('please_select_business')}</h3>
                    <p className="text-xs text-gray-500 max-w-sm mx-auto">
                        {t('select_business_to_view_assignments')}
                    </p>
                </div>
            )}

            {/* Loading state */}
            {isLoading && <Loader size="xs" color="black" />}

            {/* Results */}
            {isBusinessSelected && !isLoading && (
                <div className="space-y-4">
                    <div className="flex items-center gap-4 border-b border-gray-200 pb-2">
                        <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                            <span>{t('active_reservations_in_date', { date: formatDayLong(start) })}</span>
                            <span className="bg-sky-50 text-sky-700 text-xs font-black px-2.5 py-0.5 rounded-full">
                                {filteredReservations.length}
                            </span>
                        </h2>
                        <Button
                            size='xs'
                            color='black'
                            variant='light'
                            leftSection={<IconMailForward size={13} />}
                            onClick={() => setIsEmailModalOpen(true)}
                        >
                            {t('send_assignment_email')}
                        </Button>
                    </div>


                    {filteredReservations.length > 0 ? (
                        <ReservationsList
                            reservations={filteredReservations}
                            showFilters={false}
                            displayOptions={{
                                showClientName: true,
                                showProvider: true,
                                showCoordinator: true,
                                editable: false,
                                groupByPaxs: true,
                                showPaxs: true,
                                showPrice: true,
                                showPaymentStatus: true,
                                highlightDate: start,
                            }}
                        />
                    ) : (
                        <p className="text-xs text-gray-500 italic py-4">{t('no_reservation_assignments')}</p>
                    )}
                </div>
            )}

            {/* Send Email Modal Component */}
            <SendAssignmentMailModal
                opened={isEmailModalOpen}
                onClose={() => setIsEmailModalOpen(false)}
                business={selectedBusiness}
                date={formatDayLong(start) || start || ''}
                reservationsCount={filteredReservations.length}
            />
        </div>
    );
}
